import type {
  ConnectedDevice,
  DeviceConfigurationApi,
} from '@model/DeviceConfiguration';
import type { Device } from '@types';
import type { SpectralWorkspaceSession } from '../types';

export type SessionSensorSource = Pick<
  DeviceConfigurationApi,
  'connectedDevices' | 'connectedTo' | 'sensor_id'
>;

const SENSOR_TYPES = new Set(['smartwatch', 'heart-rate']);

const isSensorDevice = (device: ConnectedDevice) =>
  SENSOR_TYPES.has(device.type);

const matchesConnectedRef = (
  device: ConnectedDevice,
  ref: string | number | null | undefined
): boolean => {
  if (ref == null || ref === '') return false;

  const normalized = String(ref).trim();

  return (
    device.code === normalized ||
    String(device.id) === normalized ||
    device.name?.trim() === normalized
  );
};

export const buildSensorDeviceLookup = (
  devices: Device[]
): Map<string, Device> => {
  const lookup = new Map<string, Device>();

  devices.forEach(device => {
    if (device.type !== 'smartwatch') return;

    if (device.code) {
      lookup.set(device.code, device);
    }
    if (device.clientId) {
      lookup.set(device.clientId, device);
    }
    if (device.id != null) {
      lookup.set(String(device.id), device);
    }
  });

  return lookup;
};

export const findConnectedSensor = (
  data?: SessionSensorSource | null
): ConnectedDevice | null => {
  const devices = data?.connectedDevices ?? [];
  const sensors = devices.filter(isSensorDevice);

  if (sensors.length === 0) {
    return null;
  }

  const sensorId = data?.sensor_id;
  if (sensorId != null) {
    const bySensorId = sensors.find(sensor => sensor.id === sensorId);
    if (bySensorId) {
      return bySensorId;
    }
  }

  const connectedTo = data?.connectedTo?.trim();
  if (connectedTo) {
    const byConnectedTo = sensors.find(sensor =>
      matchesConnectedRef(sensor, connectedTo)
    );
    if (byConnectedTo) {
      return byConnectedTo;
    }
  }

  return sensors[0] ?? null;
};

export type SessionSensorDisplay = {
  /** Hardware model when known from the user device registry. */
  model: string | null;
  /** Friendly / assigned sensor name. */
  name: string | null;
};

export type SessionSensorFields = {
  /** Primary label for lists/sorting: model when known, otherwise name. */
  deviceName: string | null;
  /** Secondary label shown under the model in the sessions table. */
  deviceSensorName: string | null;
};

const resolveRegistryDevice = (
  sensor: ConnectedDevice,
  lookup?: Map<string, Device>
): Device | undefined =>
  (sensor.code ? lookup?.get(sensor.code) : undefined) ??
  lookup?.get(String(sensor.id));

export const formatSensorDisplay = (
  sensor: ConnectedDevice,
  lookup?: Map<string, Device>
): SessionSensorDisplay => {
  const registry = resolveRegistryDevice(sensor, lookup);
  const model = registry?.model?.trim() || null;
  const name =
    registry?.name?.trim() ||
    sensor.name?.trim() ||
    sensor.code?.trim() ||
    null;

  return { model, name };
};

export const formatSensorLabel = (
  sensor: ConnectedDevice,
  lookup?: Map<string, Device>
): string => {
  const { model, name } = formatSensorDisplay(sensor, lookup);
  return model || name || '';
};

export const toSessionSensorFields = (
  display: SessionSensorDisplay | null
): SessionSensorFields => {
  if (!display) {
    return { deviceName: null, deviceSensorName: null };
  }

  const primary = display.model || display.name;
  const secondary =
    display.model &&
    display.name &&
    display.name.localeCompare(display.model, 'it', { sensitivity: 'base' }) !==
      0
      ? display.name
      : null;

  return {
    deviceName: primary,
    deviceSensorName: secondary,
  };
};

export const resolveSessionSensorDisplay = (
  data?: SessionSensorSource | null,
  lookup?: Map<string, Device>
): SessionSensorDisplay | null => {
  const sensor = findConnectedSensor(data);
  if (sensor) {
    const display = formatSensorDisplay(sensor, lookup);
    if (!display.model && !display.name) {
      return null;
    }
    return display;
  }

  const connectedTo = data?.connectedTo?.trim();
  if (connectedTo) {
    const registry = lookup?.get(connectedTo);
    const model = registry?.model?.trim() || null;
    const name = registry?.name?.trim() || connectedTo;

    return { model, name };
  }

  return null;
};

export const resolveSessionSensorFields = (
  data?: SessionSensorSource | null,
  lookup?: Map<string, Device>
): SessionSensorFields =>
  toSessionSensorFields(resolveSessionSensorDisplay(data, lookup));

export const resolveSessionSensorLabel = (
  data?: SessionSensorSource | null,
  lookup?: Map<string, Device>
): string | null => resolveSessionSensorFields(data, lookup).deviceName;

/** @deprecated Use resolveSessionSensorLabel */
export const resolveSessionDeviceLabel = resolveSessionSensorLabel;

export const collectUniqueSessionDevices = (
  sessions: Pick<SpectralWorkspaceSession, 'deviceName'>[]
): string[] => {
  const seen = new Set<string>();
  const devices: string[] = [];

  sessions.forEach(session => {
    const label = session.deviceName?.trim();
    if (!label) return;

    const key = label.toLocaleLowerCase('it');
    if (seen.has(key)) return;

    seen.add(key);
    devices.push(label);
  });

  return devices.sort((left, right) =>
    left.localeCompare(right, 'it', { sensitivity: 'base' })
  );
};
